import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth/useAuth';
import { followCreator, subscriberQueries, unfollowCreator } from '@/lib/queries/subscriber';

export function FollowButton({
  creatorId,
  size = 'md',
}: {
  creatorId: string;
  size?: 'sm' | 'md';
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const following = useQuery(subscriberQueries.following(user?.id));
  const [busy, setBusy] = useState(false);

  const isFollowing = (following.data ?? []).includes(creatorId);
  const self = user?.id === creatorId;
  if (self) return null;

  const toggle = async () => {
    if (!user) return;
    setBusy(true);
    try {
      if (isFollowing) {
        await unfollowCreator(creatorId, user.id);
        toast.success('Unfollowed.');
      } else {
        await followCreator(creatorId, user.id);
        toast.success('Following.');
      }
      await queryClient.invalidateQueries({ queryKey: ['following', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['followed-feed', user.id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update follow');
    } finally {
      setBusy(false);
    }
  };

  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy}
      aria-pressed={isFollowing}
      className={`rounded-md ${pad} font-medium disabled:opacity-60 ${
        isFollowing
          ? 'border border-ink/20 text-ink/70 hover:border-signal hover:text-signal'
          : 'bg-ink text-parchment hover:bg-oxblood'
      }`}
    >
      {isFollowing ? 'Following' : 'Follow'}
    </button>
  );
}
